import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AdTrendResponse } from '../../types/Ad.api.type';
import { X } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

const POSITION_LABELS: Record<string, string> = {
  MAIN_FEED: '메인피드',
  MAIN_FEED_TOP: '메인피드(상단)',
  POPULAR_FEED: '인기피드',
  POPULAR_FEED_TOP: '인기피드(상단)',
  MY_FEED: '마이피드',
  MY_FEED_TOP: '마이피드(상단)',
  DETAIL_COMMENT: '상세댓글',
};

interface AdDetailModalProps {
  adId: number;
  adTitle: string;
  onClose: () => void;
}

/**
 * 광고 상세 성과 모달 (위치별 성과 + 시간대별/일별 추이)
 */
const AdDetailModal = ({ adId, adTitle, onClose }: AdDetailModalProps) => {
  const { data: adStats, isLoading } = useQuery({
    queryKey: ['adStats', adId],
    queryFn: () => adApi.getAdStats(adId).then(res => res.data),
  });

  const { data: adTrend } = useQuery({
    queryKey: ['adTrend', adId],
    queryFn: () => adApi.getAdTrend(adId).then(res => res.data as AdTrendResponse),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-gray-900 truncate pr-4">{adTitle} — 상세 성과</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="h-32 flex items-center justify-center text-gray-500">로딩 중...</div>
        ) : (
          <div className="space-y-6">
            {/* 위치별 성과 테이블 */}
            {adStats && adStats.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">위치별 성과</h4>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">위치</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">PV</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">UV</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">클릭</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">CTR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {adStats.map(row => (
                      <tr key={row.position} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700">{POSITION_LABELS[row.position] ?? row.position}</td>
                        <td className="px-3 py-2 text-right">{row.pv.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">{row.uv.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">{row.clicks.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-medium">{row.ctr.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* 오늘 시간대별 추이 */}
            {adTrend && adTrend.hourly.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">오늘 시간대별 추이</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={adTrend.hourly} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="hour" stroke="#6B7280" style={{ fontSize: '11px' }} tickFormatter={h => `${h}시`} />
                    <YAxis stroke="#6B7280" style={{ fontSize: '11px' }} width={35} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
                      labelFormatter={h => `${h}시`}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="pv" name="노출" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="clicks" name="클릭" fill="#10B981" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* 일별 추이 */}
            {adTrend && adTrend.daily.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">일별 추이</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={adTrend.daily} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '11px' }} tickFormatter={d => d.slice(5)} />
                    <YAxis stroke="#6B7280" style={{ fontSize: '11px' }} width={35} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line type="monotone" dataKey="pv" name="노출" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="clicks" name="클릭" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* 데이터 없음 */}
            {(!adStats || adStats.length === 0) && (!adTrend || (adTrend.hourly.length === 0 && adTrend.daily.length === 0)) && (
              <div className="h-32 flex items-center justify-center text-gray-500">데이터가 없습니다.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdDetailModal;
